import { useEffect, useState } from "react";
import type {
  EstadoProcesal,
  EvaluacionHistorialItem,
  Expediente,
  NivelRiesgo,
  ProbabilidadExito,
  ValorEstrategico,
} from "../../../shared/types";
import { Boton } from "../../components/Boton";
import { Campo, Select, TextArea } from "../../components/Formulario";
import { SelectorMultiple } from "../../components/SelectorMultiple";
import { Tarjeta } from "../../components/Tarjeta";
import { useToast } from "../../components/Toast";
import { api, ErrorApi } from "../../lib/apiClient";

const NIVELES_RIESGO: NivelRiesgo[] = ["Bajo", "Medio", "Alto", "Crítico"];
const PROBABILIDADES: ProbabilidadExito[] = [
  "No evaluada",
  "Muy baja",
  "Baja",
  "Media",
  "Alta",
  "Muy alta",
];

export function PestanaEvaluacion({
  expediente,
  onCambio,
}: {
  expediente: Expediente;
  onCambio: () => void;
}) {
  const { mostrar } = useToast();
  const [estadosProcesales, setEstadosProcesales] = useState<EstadoProcesal[]>([]);
  const [valoresEstrategicos, setValoresEstrategicos] = useState<ValorEstrategico[]>([]);
  const [historial, setHistorial] = useState<EvaluacionHistorialItem[]>([]);
  const [guardando, setGuardando] = useState(false);

  const [estadoProcesalId, setEstadoProcesalId] = useState<string>(expediente.estado_procesal_id ?? "");
  const [nivelRiesgo, setNivelRiesgo] = useState<string>(expediente.nivel_riesgo ?? "");
  const [fundamentoRiesgo, setFundamentoRiesgo] = useState(expediente.fundamento_riesgo ?? "");
  const [probabilidadExito, setProbabilidadExito] = useState<string>(
    expediente.probabilidad_exito ?? "No evaluada",
  );
  const [fundamentoProbabilidad, setFundamentoProbabilidad] = useState(
    expediente.fundamento_probabilidad ?? "",
  );
  const [idsSeleccionados, setIdsSeleccionados] = useState<Set<string>>(
    new Set((expediente.valoresEstrategicos ?? []).map((v) => v.id)),
  );

  useEffect(() => {
    setEstadoProcesalId(expediente.estado_procesal_id ?? "");
    setNivelRiesgo(expediente.nivel_riesgo ?? "");
    setFundamentoRiesgo(expediente.fundamento_riesgo ?? "");
    setProbabilidadExito(expediente.probabilidad_exito ?? "No evaluada");
    setFundamentoProbabilidad(expediente.fundamento_probabilidad ?? "");
    setIdsSeleccionados(new Set((expediente.valoresEstrategicos ?? []).map((v) => v.id)));
  }, [expediente]);

  useEffect(() => {
    api
      .get<{ items: EstadoProcesal[] }>("/catalogos-estudio/estados-procesales")
      .then((d) => setEstadosProcesales(d.items.filter((e) => e.activo)))
      .catch(() => undefined);
    api
      .get<{ items: ValorEstrategico[] }>("/catalogos-estudio/valores-estrategicos")
      .then((d) => setValoresEstrategicos(d.items.filter((v) => v.activo)))
      .catch(() => undefined);
    cargarHistorial();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expediente.id]);

  function cargarHistorial() {
    api
      .get<{ items: EvaluacionHistorialItem[] }>(`/expedientes/${expediente.id}/evaluacion/historial`)
      .then((d) => setHistorial(d.items))
      .catch(() => undefined);
  }

  function alternarValorEstrategico(id: string) {
    setIdsSeleccionados((prev) => {
      const nuevo = new Set(prev);
      if (nuevo.has(id)) nuevo.delete(id);
      else nuevo.add(id);
      return nuevo;
    });
  }

  async function guardar() {
    setGuardando(true);
    try {
      await api.put(`/expedientes/${expediente.id}/evaluacion`, {
        estado_procesal_id: estadoProcesalId || null,
        nivel_riesgo: nivelRiesgo || null,
        fundamento_riesgo: fundamentoRiesgo || null,
        probabilidad_exito: probabilidadExito,
        fundamento_probabilidad: fundamentoProbabilidad || null,
      });
      await api.put(`/expedientes/${expediente.id}/valores-estrategicos`, {
        valorEstrategicoIds: [...idsSeleccionados],
      });
      mostrar("Evaluación actualizada.", "exito");
      cargarHistorial();
      onCambio();
    } catch (err) {
      mostrar(
        err instanceof ErrorApi ? err.message : "No se pudo guardar la evaluación.",
        "error",
      );
    } finally {
      setGuardando(false);
    }
  }

  return (
    <div className="space-y-6">
      <Tarjeta className="space-y-4 p-5">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Campo etiqueta="Estado procesal">
            <Select
              value={estadoProcesalId}
              onChange={(e) => setEstadoProcesalId(e.target.value)}
            >
              <option value="">Sin asignar</option>
              {estadosProcesales.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.nombre}
                </option>
              ))}
            </Select>
          </Campo>
          <Campo etiqueta="Nivel de riesgo">
            <Select value={nivelRiesgo} onChange={(e) => setNivelRiesgo(e.target.value)}>
              <option value="">Sin evaluar</option>
              {NIVELES_RIESGO.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </Select>
          </Campo>
        </div>
        <Campo
          etiqueta="Fundamento del riesgo"
        >
          <TextArea
            rows={2}
            value={fundamentoRiesgo}
            onChange={(e) => setFundamentoRiesgo(e.target.value)}
            placeholder="Motivo profesional de la evaluación de riesgo (opcional)."
          />
        </Campo>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Campo etiqueta="Probabilidad de éxito">
            <Select
              value={probabilidadExito}
              onChange={(e) => setProbabilidadExito(e.target.value)}
            >
              {PROBABILIDADES.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </Select>
          </Campo>
        </div>
        <p className="text-xs text-texto-secundario">
          Evaluación interna del equipo, no es una garantía de resultado. No se muestra al cliente.
        </p>
        <Campo etiqueta="Fundamento de la probabilidad">
          <TextArea
            rows={2}
            value={fundamentoProbabilidad}
            onChange={(e) => setFundamentoProbabilidad(e.target.value)}
            placeholder="Motivo profesional de la probabilidad estimada (opcional)."
          />
        </Campo>

        <div>
          <span className="mb-2 block text-sm font-medium text-texto">Valor estratégico</span>
          <SelectorMultiple
            opciones={valoresEstrategicos.map((v) => ({ id: v.id, etiqueta: v.nombre }))}
            seleccionados={idsSeleccionados}
            onCambiar={alternarValorEstrategico}
            vacio="No hay valores estratégicos configurados en el estudio."
          />
        </div>

        <div className="flex justify-end pt-2">
          <Boton onClick={guardar} cargando={guardando}>
            Guardar evaluación
          </Boton>
        </div>
      </Tarjeta>

      <Tarjeta>
        <h3 className="mb-3 text-sm font-semibold text-texto">Historial de cambios</h3>
        {historial.length === 0 ? (
          <p className="text-sm text-texto-secundario">Todavía no hay cambios registrados.</p>
        ) : (
          <ul className="space-y-2">
            {historial.map((h) => (
              <li key={h.id} className="border-b border-borde pb-2 text-sm last:border-0">
                <span className="font-medium text-texto">{etiquetaCampo(h.campo)}</span>{" "}
                <span className="text-texto-secundario">
                  {h.valor_anterior ?? "—"} → {h.valor_nuevo ?? "—"}
                </span>
                <div className="text-xs text-texto-secundario">
                  {h.usuario_nombre ?? "Usuario"} · {h.creado_en}
                </div>
              </li>
            ))}
          </ul>
        )}
      </Tarjeta>
    </div>
  );
}

function etiquetaCampo(campo: string): string {
  const etiquetas: Record<string, string> = {
    estado_procesal: "Estado procesal",
    nivel_riesgo: "Nivel de riesgo",
    fundamento_riesgo: "Fundamento del riesgo",
    probabilidad_exito: "Probabilidad de éxito",
    fundamento_probabilidad: "Fundamento de la probabilidad",
    valor_estrategico: "Valor estratégico",
  };
  return etiquetas[campo] ?? campo;
}
